import { type LoyaltyUserRegistrationInfo } from "@koala/sdk";
import { Form, Formik } from "formik";
import { boolean, lazy, object, string } from "yup";
import { StyledSubmitButtonRow } from "./styles";
import StringAccessor from "@/components/cmsConfig/stringAccessor";
import { styled } from "@/components/content/stitches";
import { StyledCheckbox } from "@/components/uielements/checkbox/styles";
import { FavoriteLocationDropdownField } from "@/components/uielements/favoriteLocationsDropdownField/favorite-locations-dropdown-field";
import { StyledFormField } from "@/components/uielements/form-fields/styles";
import { Input } from "@/components/uielements/formik";
import { StyledPrimaryButton } from "@/components/uielements/primaryButton/styles";
import { SIGNUP_OPTIONAL_FIELDS } from "@/constants/authentication";
import { FEATURE_FLAGS } from "@/constants/features";
import {
  REGEX,
  REGEX_VALIDATION_STRINGS,
  VALIDATION_STRINGS,
} from "@/constants/validation";
import { useSelector } from "@/redux";
import { getSignupOptionalFields } from "@/utils/auth";
import { KoalaFeatureHandler } from "@/utils/features";

interface Props {
  loading: boolean;
  doSignUp: (values: SignUpFormFieldValues) => void;
}

export interface SignUpFormFieldValues
  extends Partial<LoyaltyUserRegistrationInfo> {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  password: string;
  zip_code?: string;
  invite_code?: string;
  birthday?: string;
  terms_and_conditions: boolean;
  favorite_location_id?: string;
  opt_in?: boolean;
}

export function SignUpForm({ doSignUp, loading }: Props) {
  const organization = useSelector((state) => state.app.organization);
  const { strings, webConfig } = useSelector((state) => state.app.cmsConfig);
  const loyaltyDriverID = organization.organization.loyalty_driver_id;

  const appState = useSelector((state) => state.app);
  const FeatureHandler = new KoalaFeatureHandler(appState);

  const isEnabled = (field: SIGNUP_OPTIONAL_FIELDS) => {
    const registrationFields =
      organization.external_auth.registration_fields.user;
    const optionalFields = getSignupOptionalFields(loyaltyDriverID);
    // @ts-expect-error field can't index `accounts`.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const cmsConfigFieldValue = webConfig.accounts[field];

    if (
      !registrationFields?.includes(field) &&
      !optionalFields?.includes(field)
    ) {
      return false;
    }

    // Check CMS config
    if (cmsConfigFieldValue === false || cmsConfigFieldValue === "disabled") {
      return false;
    }

    return true;
  };

  const initialValues: SignUpFormFieldValues = {
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
    zip_code: undefined,
    invite_code: undefined,
    birthday: undefined,
    terms_and_conditions: false,
    favorite_location_id: undefined,
  };

  // only add a value for opt_in if the field is enabled
  if (isEnabled(SIGNUP_OPTIONAL_FIELDS.OPT_IN) && strings?.signup.opt_in) {
    initialValues.opt_in = webConfig.accounts?.opt_in_auto_select;
  }

  // Validation schema for the reset form.
  const validationSchema = object({
    first_name: string().required(VALIDATION_STRINGS.REQUIRED),
    last_name: string().required(VALIDATION_STRINGS.REQUIRED),
    email: string()
      .email(VALIDATION_STRINGS.EMAIL)
      .required(VALIDATION_STRINGS.REQUIRED),
    phone: string()
      .required(VALIDATION_STRINGS.REQUIRED)
      .matches(REGEX.PHONE, REGEX_VALIDATION_STRINGS.PHONE),
    password: string()
      .required(VALIDATION_STRINGS.REQUIRED)
      .min(8, VALIDATION_STRINGS.PASSWORD_LENGTH),
    zip_code: lazy(() =>
      (webConfig.accounts.zip_code === "required"
        ? string().required(VALIDATION_STRINGS.REQUIRED)
        : string().optional()
      ).matches(REGEX.ZIP_CODE_USA_AND_POSTAL_CODE_CANADA, {
        message: REGEX_VALIDATION_STRINGS.ZIP_CODE_USA_AND_POSTAL_CODE_CANADA,
      })
    ),
    favorite_location_id: lazy(() =>
      webConfig.accounts.favorite_location === "required" &&
      FeatureHandler.isFeatureAllowed(
        FEATURE_FLAGS.ACCOUNT__FAVORITE_LOCATIONS_ENABLED
      )
        ? string().required(VALIDATION_STRINGS.REQUIRED)
        : string().optional()
    ),
    invite_code: lazy(() => string().optional()),
    birthday: lazy(() =>
      (isEnabled(SIGNUP_OPTIONAL_FIELDS.BIRTHDAY)
        ? string().required(VALIDATION_STRINGS.REQUIRED)
        : string().optional()
      ).matches(
        REGEX.MM_DD_YYYY_DASHES_SLASHES,
        REGEX_VALIDATION_STRINGS.MM_DD_YYYY_DASHES_SLASHES
      )
    ),
    opt_in: lazy(() =>
      isEnabled(SIGNUP_OPTIONAL_FIELDS.OPT_IN) && strings?.signup.opt_in
        ? boolean().required(VALIDATION_STRINGS.REQUIRED)
        : boolean().optional()
    ),
    terms_and_conditions: lazy(() =>
      isEnabled(SIGNUP_OPTIONAL_FIELDS.TERMS_AND_CONDITIONS) &&
      strings?.signup.accept_terms_conditions
        ? boolean().oneOf([true], VALIDATION_STRINGS.REQUIRED)
        : boolean().optional()
    ),
  });

  const accessor = loading ? "signup.cta_loading" : "signup.cta";

  return (
    <Formik
      onSubmit={doSignUp}
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {({ handleChange, errors, touched }) => (
        <Form data-testid="sign-up-form">
          <Row>
            <StyledFormField>
              <Input
                label={""}
                id="first_name"
                name="first_name"
                placeholder="First Name"
                type="text"
              />
            </StyledFormField>
            <StyledFormField>
              <Input
                label={""}
                id="last_name"
                name="last_name"
                placeholder="Last Name"
                type="text"
              />
            </StyledFormField>
          </Row>

          <Row>
            <StyledFormField>
              <Input
                label={""}
                id="email"
                name="email"
                placeholder="Email"
                type="text"
              />
            </StyledFormField>

            <StyledFormField>
              <Input
                label={""}
                id="phone"
                name="phone"
                type="tel"
                placeholder="Phone Number"
              />
            </StyledFormField>
          </Row>

          <Row>
            <StyledFormField>
              <Input
                label={""}
                id="password"
                name="password"
                type="password"
                placeholder="Password"
                autoComplete="off"
              />
            </StyledFormField>

            {isEnabled(SIGNUP_OPTIONAL_FIELDS.ZIP_CODE) ? (
              <StyledFormField>
                <Input
                  label={""}
                  id="zip_code"
                  name="zip_code"
                  type="text"
                  placeholder="Zip Code"
                  maxLength={10}
                />
              </StyledFormField>
            ) : null}
          </Row>

          <Row>
            {isEnabled(SIGNUP_OPTIONAL_FIELDS.REFERRAL_CODE) ? (
              <StyledFormField>
                <Input
                  label={""}
                  id="invite_code"
                  name="invite_code"
                  type="text"
                  placeholder="Referral Code (optional)"
                />
              </StyledFormField>
            ) : null}

            {isEnabled(SIGNUP_OPTIONAL_FIELDS.BIRTHDAY) ? (
              <StyledFormField>
                <Input
                  label={""}
                  id="birthday"
                  name="birthday"
                  type="text"
                  placeholder="Birthday"
                />
              </StyledFormField>
            ) : null}
          </Row>

          <Row>
            <FavoriteLocationDropdownField
              input={{
                name: "favorite_location_id",
                placeholder: "favorite_location_id",
              }}
              handleChange={handleChange}
              // TODO: When we move Favorite Location Dropdown field into formik input
              // we might want to how we show error here
              // NOTE: Added touched to be able to surface error only when field has been touched
              errorMessage={
                touched.favorite_location_id
                  ? errors.favorite_location_id
                  : undefined
              }
            />
          </Row>

          <Row>
            {isEnabled(SIGNUP_OPTIONAL_FIELDS.TERMS_AND_CONDITIONS) &&
            strings?.signup.accept_terms_conditions ? (
              <Checkbox>
                <Label
                  htmlFor="terms_and_conditions"
                  className="control control--checkbox"
                >
                  <StringAccessor
                    accessor="signup.accept_terms_conditions"
                    html
                    dataObj={{
                      brandName: organization.organization.label,
                    }}
                  />
                  <Input
                    id="terms_and_conditions"
                    name="terms_and_conditions"
                    type="checkbox"
                    label={strings.signup.accept_terms_conditions}
                  />
                  <div
                    data-testid="terms-and-conditions-checkbox"
                    className="control__indicator"
                  ></div>
                </Label>
              </Checkbox>
            ) : null}
          </Row>

          <Row>
            {isEnabled(SIGNUP_OPTIONAL_FIELDS.OPT_IN) &&
            strings?.signup.opt_in ? (
              <Checkbox>
                <Label htmlFor="opt_in" className="control control--checkbox">
                  <StringAccessor
                    accessor="signup.opt_in"
                    html
                    dataObj={{
                      brandName: organization.organization.label,
                    }}
                  />
                  <Input
                    id="opt_in"
                    name="opt_in"
                    type="checkbox"
                    label={strings.signup.opt_in}
                  />
                  <div
                    data-testid="opt-in-checkbox"
                    className="control__indicator"
                  ></div>
                </Label>
              </Checkbox>
            ) : null}
          </Row>

          <StyledSubmitButtonRow>
            <StyledPrimaryButton
              data-testid="signup-button"
              type="submit"
              submitting={loading}
              buttonTheme="secondary"
            >
              <StringAccessor accessor={accessor} />
            </StyledPrimaryButton>
          </StyledSubmitButtonRow>
        </Form>
      )}
    </Formik>
  );
}

const Row = styled("div", {
  display: "flex",
  flexWrap: "wrap",
});

const Label = styled("label", {
  display: "flex",
});

const Checkbox = styled(StyledCheckbox, {
  "& a": {
    color: "$accentPrimary",
    fontSize: "$primary",
  },
  "& div[role=alert]": {
    top: "$4",
    left: "0",
  },
});
